if (!customElements.get("discount-form")) {
  class DiscountForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[name="discount"]');
      if (!this.input) {
        console.error(
          "DiscountForm Error: Could not find initial 'input' element.",
        );
        return;
      }
      this.addEventListener("click", this.handleClick.bind(this));
    }

    handleClick(event) {
      const applyButton = event.target.closest('[data-component="apply"]');
      const removeButton = event.target.closest('[data-component="remove"]');
      if (!applyButton && !removeButton) return;

      event.preventDefault();
      if (this.classList.contains("loading")) return;

      const feedbackEl = this.querySelector('[data-component="feedback"]');
      const existingCodeElements = this.querySelectorAll(
        ".discount-form__button-text",
      );
      const existingCodes = [...existingCodeElements].map((el) =>
        el.textContent.trim(),
      );

      let codesToUpdate;
      let action = "remove";

      if (applyButton) {
        action = "apply";
        const newCode = this.input.value.trim();
        if (!newCode) {
          if (feedbackEl)
            feedbackEl.textContent = "Please enter a discount code.";
          return;
        }
        const allCodes = new Set([...existingCodes, newCode]);
        codesToUpdate = [...allCodes].join(",");
      } else if (removeButton) {
        const codeToRemove = removeButton
          .querySelector(".discount-form__button-text")
          .textContent.trim();
        const remainingCodes = existingCodes.filter(
          (code) => code !== codeToRemove,
        );
        codesToUpdate = remainingCodes.join(",");
      }

      this.updateDiscount(codesToUpdate, action);
    }

    isValidPromoCode(discount) {
      return discount && discount?.applicable;
    }

    async updateDiscount(discountCodes, action) {
      this.classList.add("loading");
      const initialFeedbackEl = this.querySelector(
        '[data-component="feedback"]',
      );
      if (initialFeedbackEl) initialFeedbackEl.textContent = "";

      // We now use the cart/update.js endpoint which just updates the cart data.
      const body = JSON.stringify({ discount: discountCodes });

      try {
        const updateResponse = await fetch("/cart/update.js", {
          ...fetchConfig(),
          body,
        });
        const updateState = await updateResponse.json();

        if (!updateResponse.ok) {
          const errorMessage =
            updateState.description ||
            updateState.message ||
            "An unknown error occurred.";
          throw new Error(errorMessage);
        }

        // Always dispatch the event on a successful update, regardless of action.
        // We will verify the 'apply' action specifically below.

        // Logic to check if the code was actually applied/removed (optimistic check)
        // If action was 'apply' and it's NOT in the cart, it failed (invalid code).
        // If action was 'remove', we assume success.
        let success = true;

        if (action === "apply") {
          const attemptedCode = this.input.value.trim();
          await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for DB sync

          const newCartFetch = await fetch(`${window.routes.cart_url}`, {
            headers: {
              "Content-Type": "application/json",
              Accept: `application/json`,
            },
          });
          const newCartState = await newCartFetch.json();

          const newFeedbackEl = this.querySelector(
            '[data-component="feedback"]',
          );
          const newInputEl = this.querySelector('input[name="discount"]');

          const applicableApps = newCartState.discount_codes || [];

          const isApplied = applicableApps.some((app) => {
            if (app.code != attemptedCode) {
              return false;
            }
            return (
              discountCodes.includes(app.code) && this.isValidPromoCode(app)
            );
          });

          if (isApplied) {
            newInputEl.value = "";
            if (newFeedbackEl) {
              newFeedbackEl.classList.remove("error");
              newFeedbackEl.textContent = "";
            }
            success = true;
          } else {
            if (newFeedbackEl) {
              newFeedbackEl.classList.add("error");
              newFeedbackEl.textContent = window.limitless_cart_strings
                ? window.limitless_cart_strings.discount_error
                : "The discount code is invalid or not applicable to the items in your cart.";
            }
            success = false;
          }
        }

        // Only dispatch update events if the action was successful (remove always success, apply needs check)
        if (success) {
          this.dispatchEvent(
            new CustomEvent("cart:updated", { bubbles: true }),
          );
          if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "discount-form",
              cartData: updateState,
            });
          }
        }
      } catch (error) {
        console.error("ERror");
        const finalFeedbackEl = this.querySelector(
          '[data-component="feedback"]',
        );
        if (finalFeedbackEl) finalFeedbackEl.textContent = error.message;
        console.error(`DiscountForm Error: ${error.message}`);
      } finally {
        this.classList.remove("loading");
      }
    }
  }

  customElements.define("discount-form", DiscountForm);
}
